import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { completeBooking } from '../api/endpoints/bookings';
import { acknowledgeCue, getBookingCue } from '../api/endpoints/cues';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { Booking } from '../api/schemas';
import { usePermissions } from '../auth/usePermissions';
import { applyBookingToCaches } from '../lib/bookings';
import { bookingFacts, bookingTitle } from '../lib/bookingPresentation';
import { useBusiness } from '../lib/useBusiness';
import { AgendaRow } from './AgendaRow';
import { ConfirmAction } from './ConfirmAction';
import { CueBanner } from './CueBanner';

type BookingRowProps = {
  booking: Booking;
  /** 'Ayer' or a date word when the row renders outside its own day's section. */
  dayNote?: string;
  /** Only the Por cerrar strip offers the inline action; elsewhere the row is just a link. */
  showComplete?: boolean;
};

/**
 * One booking, wired to its cue and the "mark complete" action. Cues only ever
 * fire for confirmed bookings, so the query stays off everywhere else instead
 * of polling once per row.
 */
export function BookingRow({ booking, dayNote, showComplete = false }: BookingRowProps) {
  const queryClient = useQueryClient();
  const business = useBusiness();
  // The cue is what Dale knows about the customer: the API refuses it to a profile without Clientes (a waiter's),
  // so the row does not even ask.
  const { can } = usePermissions();
  const acknowledgeKey = useRef(createIdempotencyKeyHolder()).current;
  const completeKey = useRef(createIdempotencyKeyHolder()).current;

  const cueQuery = useQuery({
    queryKey: ['booking-cue', booking.id],
    queryFn: () => getBookingCue(booking.id),
    enabled: booking.status === 'confirmed' && can('clientes'),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (cueId: string) => acknowledgeCue(booking.id, cueId, acknowledgeKey.ensure()),
    onSuccess: () => {
      queryClient.setQueryData(['booking-cue', booking.id], { cue: null });
    },
    onSettled: () => acknowledgeKey.reset(),
  });

  const completeMutation = useMutation({
    mutationFn: () => completeBooking(booking.id, completeKey.ensure()),
    onSuccess: (completed) => {
      applyBookingToCaches(queryClient, completed);
    },
    onSettled: () => completeKey.reset(),
  });

  const cue = cueQuery.data?.cue;
  const offerComplete = showComplete && booking.status === 'confirmed';

  return (
    <AgendaRow
      booking={booking}
      title={bookingTitle(booking, business)}
      facts={bookingFacts(booking, business)}
      {...(dayNote !== undefined ? { dayNote } : {})}
    >
      {cue || offerComplete ? (
        <>
          {cue ? (
            <CueBanner
              cue={cue}
              onAcknowledge={() => acknowledgeMutation.mutate(cue.cueId)}
              acknowledging={acknowledgeMutation.isPending}
            />
          ) : null}
          {offerComplete ? (
            <ConfirmAction
              label="Marcar completada"
              variant="secondary"
              onConfirm={() => completeMutation.mutate()}
              pending={completeMutation.isPending}
            />
          ) : null}
        </>
      ) : null}
    </AgendaRow>
  );
}
